import type { IntegerAmount } from '@actual-app/core/shared/util';

import { faltante } from './objetivos';
import type { DatosCategoriaMes } from './objetivos';

/**
 * Botón principal del teclado de asignar (H28): una sola acción, la más
 * probable para la categoría, a todo el ancho bajo la fila de atajos.
 *
 * Funciones puras, sin React; el teclado (`AssignKeypad.tsx`) las pinta.
 */
export type BotonTeclado =
  /** Disponible negativo: abre la hoja «Cubrir» con esta categoría. */
  | { tipo: 'cubrir'; importe: IntegerAmount }
  /**
   * Con objetivo y le falta: pone lo que falta sin cerrar el teclado.
   * `objetivo` es en lo que quedará lo asignado (o el disponible, si el
   * objetivo se mide sobre el saldo).
   */
  | { tipo: 'asignar-falta'; importe: IntegerAmount; objetivo: IntegerAmount }
  /** Sin objetivo: repite lo asignado el mes pasado. */
  | { tipo: 'como-mes-pasado'; importe: IntegerAmount }
  /** Sin objetivo y nada que repetir: abre el editor de objetivo. */
  | { tipo: 'crear-objetivo' };

export type EntradaBotonTeclado = {
  datos: DatosCategoriaMes;
  /** Lo asignado el mes anterior (céntimos); `null` si no se sabe. */
  asignadoMesPasado: IntegerAmount | null;
  /** La hoja «Cubrir» solo existe en el presupuesto de sobres. */
  puedeCubrir: boolean;
  /** Plantillas de objetivo activas: sin ellas no se ofrece «Crear objetivo». */
  objetivosActivos: boolean;
};

/**
 * Qué botón principal enseña el teclado, por prioridad: saldo negativo >
 * le falta para el objetivo > sin objetivo. Con el objetivo cumplido (o la
 * categoría ignorada este mes) no hay botón: nunca uno gris que «parece
 * roto».
 */
export function botonTeclado({
  datos,
  asignadoMesPasado,
  puedeCubrir,
  objetivosActivos,
}: EntradaBotonTeclado): BotonTeclado | null {
  if (puedeCubrir && datos.balance < 0) {
    return { tipo: 'cubrir', importe: -datos.balance };
  }
  if (datos.goal != null) {
    const falta = faltante(datos);
    return falta > 0
      ? { tipo: 'asignar-falta', importe: falta, objetivo: datos.goal }
      : null;
  }
  const mesPasado = asignadoMesPasado ?? 0;
  // Si ya está asignado lo mismo que el mes pasado, repetirlo no haría nada.
  if (mesPasado > 0 && mesPasado !== datos.budgeted) {
    return { tipo: 'como-mes-pasado', importe: mesPasado };
  }
  return objetivosActivos ? { tipo: 'crear-objetivo' } : null;
}

/**
 * Línea secundaria del encabezado del teclado: el disponible si va en rojo,
 * el objetivo si lo tiene y, si no, «Sin objetivo» con lo del mes pasado.
 */
export type LineaTeclado =
  | { tipo: 'disponible'; importe: IntegerAmount }
  | { tipo: 'objetivo'; importe: IntegerAmount }
  | { tipo: 'sin-objetivo'; mesPasado: IntegerAmount | null };

export function lineaTeclado(
  datos: DatosCategoriaMes,
  asignadoMesPasado: IntegerAmount | null,
): LineaTeclado {
  if (datos.balance < 0) {
    return { tipo: 'disponible', importe: datos.balance };
  }
  if (datos.goal != null) {
    return { tipo: 'objetivo', importe: datos.goal };
  }
  return {
    tipo: 'sin-objetivo',
    mesPasado:
      asignadoMesPasado != null && asignadoMesPasado > 0
        ? asignadoMesPasado
        : null,
  };
}
